
import { supabase } from "@/integrations/supabase/client";

interface EmailApplicationData {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  business_name: string;
  membership_type: string;
  application_date: string;
  // Fields needed for PDF generation and email notifications
  business_address_street1?: string;
  business_address_street2?: string | null;
  business_address_city?: string;
  business_address_postal_code?: string;
  home_address_street1?: string;
  home_address_street2?: string | null;
  home_address_city?: string;
  home_address_postal_code?: string;
  mobile_area_code?: string;
  mobile_number?: string;
  business_area_code?: string;
  business_number?: string;
  current_potato_area?: string | null;
  total_potato_area?: string | null;
  planned_potato_area?: string | null;
  signature?: string | null;
}

export const sendConfirmationEmails = async (applicationData: EmailApplicationData) => {
  try {
    console.log("Sending confirmation emails for application:", applicationData.id);
    
    const { data: emailData, error: emailError } = await supabase.functions.invoke("send-membership-email", {
      body: { 
        application: applicationData
      },
    });

    if (emailError) {
      console.error("Error sending email notification:", emailError);
      return { success: false, error: emailError, emailSendingIssue: true };
    } 
    
    if (emailData && (!emailData.applicantEmailSent || !emailData.adminEmailSent)) {
      console.warn("Email sending issues:", emailData);
      return { success: true, emailSendingIssue: true, emailData };
    }
    
    console.log("Email confirmation successfully sent to both applicant and admin");
    return { 
      success: true, 
      emailSendingIssue: false, 
      pdfAttached: emailData?.pdfAttached || false,
      emailData 
    };
  } catch (emailFunctionError: any) {
    console.error("Error invoking email function:", emailFunctionError);
    return { success: false, error: emailFunctionError, emailSendingIssue: true };
  }
};

// Function to manually trigger a SpudBot report
export const sendManualChatbotReport = async () => {
  try {
    // Calculate date range for last 7 days - make sure to include the full current day
    const endDate = new Date();
    // Set time to end of day to include all of today's conversations
    endDate.setHours(23, 59, 59, 999);
    
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 7);
    // Set time to start of day
    startDate.setHours(0, 0, 0, 0);
    
    console.log(`Manually triggering SpudBot report for ${startDate.toISOString()} to ${endDate.toISOString()}`);
    
    // Try to get latest conversation count
    let conversationCount = 0;
    let conversationsText = "This is a manually triggered test report.";
    
    try {
      // Get session count for the period
      const { count: sessionCount, error: countError } = await supabase
        .from("chat_messages")
        .select("session_id", { count: "exact", head: true })
        .gte("created_at", startDate.toISOString())
        .lte("created_at", endDate.toISOString())
        .is("user_id", null); // Include anonymous conversations
      
      if (!countError && sessionCount !== null) {
        conversationCount = sessionCount;
        
        // Get sample of recent conversations if count > 0
        if (conversationCount > 0) {
          const { data: recentSessions } = await supabase
            .from("chat_messages")
            .select("session_id, content, role, created_at")
            .gte("created_at", startDate.toISOString())
            .lte("created_at", endDate.toISOString())
            .order("created_at", { ascending: false })
            .limit(30);
          
          if (recentSessions && recentSessions.length > 0) {
            // Group by session and format
            const sessionMap = new Map();
            recentSessions.forEach(msg => {
              if (!sessionMap.has(msg.session_id)) {
                sessionMap.set(msg.session_id, []);
              }
              sessionMap.get(msg.session_id).push(msg);
            });
            
            // Format conversations for display
            conversationsText = Array.from(sessionMap.entries())
              .map(([sessionId, messages]) => {
                const sortedMessages = [...messages].sort((a, b) => 
                  new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
                );
                
                return `--- Conversation ID: ${sessionId.substring(0, 8)} ---
Time: ${new Date(sortedMessages[0].created_at).toLocaleString()}
${sortedMessages.map(m => `${m.role}: ${m.content}`).join('\n')}`;
              })
              .join('\n\n');
          }
        }
      }
    } catch (dataError) {
      console.error("Error getting conversation data:", dataError);
      // Continue with default values if data fetching fails
    }
    
    // Adding more detailed console logs for debugging
    console.log("Invoking edge function with payload:", {
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString(),
      conversation_count: conversationCount,
      conversations: conversationsText
    });
    
    const { data, error } = await supabase.functions.invoke("send-chatbot-report", {
      body: { 
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
        conversation_count: conversationCount,
        conversations: conversationsText
      },
    });

    if (error) {
      console.error("Error triggering SpudBot report:", error);
      console.error("Error details:", JSON.stringify(error, null, 2));
      return { success: false, error };
    }
    
    console.log("Manual SpudBot report trigger response:", JSON.stringify(data, null, 2));
    return { success: true, data };
  } catch (error: any) {
    console.error("Exception triggering SpudBot report:", error);
    console.error("Error stack:", error?.stack);
    console.error("Error details:", JSON.stringify(error, null, 2));
    return { success: false, error };
  }
};

// Add a new function to check Supabase function configuration
export const checkChatbotReportFunction = async () => {
  try {
    console.log("Checking edge function configuration for send-chatbot-report");
    
    // This is a simple ping to check if the function exists and is accessible
    const { data, error } = await supabase.functions.invoke("send-chatbot-report", {
      body: { ping: true },
    });
    
    if (error) {
      console.error("Error accessing edge function:", error);
      return { success: false, error, message: "Edge function is not accessible" };
    }
    
    console.log("Edge function is accessible:", data);
    return { success: true, data, message: "Edge function is accessible" };
  } catch (error: any) {
    console.error("Exception checking edge function:", error);
    return { success: false, error, message: "Exception when checking edge function" };
  }
};
